/* eslint-disable complexity */
import {
  Box,
  Center,
  HStack,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Spinner,
  Text,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  faCaretDown,
  faCaretRight,
  faCheckCircle,
  faChevronDown,
  faCircleDot,
  faCogs,
  faCube,
  faDrawPolygon,
  faEllipsis,
  faEye,
  faEyeSlash,
  faObjectGroup,
  faObjectUngroup,
  faPen,
  faPencil,
  faTableList,
  faTimesCircle,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import React, {
  memo,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as PolylineIcon } from '../../assets/icons/draw-polyline.svg';
import SparkelIcon from '../common/icon/SparkelIcon';
import { ViewerMode } from '../common/viewer-mode';
import { useDiffing } from '../project/project-history/hooks/useDiffing';
import { DiffBadge } from '../project/project-history/DiffBadge';
import { DiffText } from '../project/project-history/DiffText';
import { EditAutoshapePopover } from './EditAutoshapePopover';
import {
  VirtualFolderNode,
  isVirtualFolderNode,
  getDisplayName,
  getParentPath,
  canDropFolderOnTarget,
  computeNewPath,
} from './folder-hierarchy-utils';
import { useEnabledSheetViewerModeValue } from 'src/components/viewers/common/hooks/useEnabledSheetViewerModeValue';
import {
  AutoShapeDeepFragment,
  AutoShapeStatus,
  EventType,
  ShapeDeepFragment,
  ShapeFolderDeepFragment,
  SheetShapeDeepFragment,
} from 'src/gql/graphql';
import { ChakraColor } from 'src/utils/color-util';
import { useUserTenant } from 'src/services/auth-info';

export type ShapeNodeType =
  | ShapeDeepFragment
  | SheetShapeDeepFragment
  | ShapeFolderDeepFragment
  | AutoShapeDeepFragment
  | VirtualFolderNode;

export type ShapeItemDiff = {
  eventType?: EventType;
  oldName?: string;
  newName?: string;
};

/**
 * Represents the data transferred during a drag operation.
 */
export type DragData =
  | { type: 'shapes'; shapes: (ShapeDeepFragment | SheetShapeDeepFragment)[] }
  | {
      type: 'folder';
      folder: ShapeFolderDeepFragment;
      folderPath: string;
    }
  | {
      type: 'autoshape';
      autoShape: AutoShapeDeepFragment;
      autoShapePath: string;
    };

type ShapeItemProps = {
  node: ShapeNodeType;
  expanded?: boolean;
  setExpanded?: (shapedId: string) => void;
  isSelected: boolean;
  allChildrenSelected?: boolean;
  allChildrenHidden?: boolean;
  onShapeSelect: (node: ShapeNodeType) => void;
  shapeChildren?: ShapeItemProps[];
  hasChildren?: boolean;
  top?: number;
  height?: string | number;
  onMoveNode: (
    draggedNodeIds: (ShapeDeepFragment | SheetShapeDeepFragment)[],
    targetNode: ShapeFolderDeepFragment | null
  ) => void;
  /** Callback when a folder is moved into another folder (renames the folder with new path) */
  onMoveFolder?: (folderId: string, newPath: string) => Promise<void>;
  /** Callback when an autoshape is moved into another folder (renames the autoshape with new path) */
  onMoveAutoShape?: (autoShapeId: string, newPath: string) => Promise<void>;
  onRename: (node: ShapeNodeType, newName: string) => void;
  onShow: (node: ShapeNodeType) => void;
  onHide: (node: ShapeNodeType) => void;
  onDelete: (node: ShapeNodeType) => void;
  level?: number;
  isHidden: boolean;
  setPageNumber: (pageNumber: number) => void;
  changeActiveSheet: (sheetId: string) => void;
  editBIMShape: (shapeToEdit: string) => void;
  editSheetShape: (shapeToEdit: string) => void;
  setViewerMode: (mode: ViewerMode) => void;
  removeShapesFromFolder: (node: ShapeNodeType) => Promise<void>;
  deleteFolder: (id: string) => Promise<void>;
  groupShapesIntoFolder: () => Promise<void>;
  /** Whether grouping is allowed for current selection (all shapes have same parent) */
  canGroupSelectedShapes?: boolean;
  selectedShapesRef: MutableRefObject<
    (ShapeDeepFragment | SheetShapeDeepFragment)[]
  >;
  isDiffing?: boolean;
  diff?: ShapeItemDiff;
  /** Display name to show in the UI (last segment of path for hierarchical folders) */
  displayName?: string;
  /** If true, automatically start in rename mode (used for newly created folders) */
  autoStartRename?: boolean;
  /** Callback when rename mode ends (used to clear the autoStartRename state in parent) */
  onRenameComplete?: () => void;
  /** Callback to ungroup a folder (move contents up one level, delete folder) */
  ungroupFolder?: (folderId: string) => Promise<void>;
};

// eslint-disable-next-line complexity
function ShapeItem({
  node,
  expanded,
  setExpanded,
  isSelected,
  allChildrenSelected,
  allChildrenHidden,
  onShapeSelect,
  onMoveNode,
  onMoveFolder,
  onMoveAutoShape,
  onRename,
  onShow,
  onHide,
  onDelete,
  shapeChildren,
  level = 0,
  isHidden,
  hasChildren,
  setPageNumber,
  changeActiveSheet,
  editBIMShape,
  editSheetShape,
  setViewerMode,
  removeShapesFromFolder,
  deleteFolder,
  groupShapesIntoFolder,
  canGroupSelectedShapes,
  selectedShapesRef,
  top,
  height,
  isDiffing,
  diff,
  displayName,
  autoStartRename,
  onRenameComplete,
  ungroupFolder,
}: ShapeItemProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);

  // Use displayName if provided, otherwise extract from node.name for hierarchical display
  const effectiveDisplayName = displayName ?? getDisplayName(node.name);
  const parentPath = getParentPath(node.name);

  // For renaming, we only edit the last segment (display name), not the full path
  const [newName, setNewName] = useState(effectiveDisplayName);

  // Auto-start rename mode when autoStartRename is true (for newly created folders)
  useEffect(() => {
    if (autoStartRename) {
      setIsRenaming(true);
      setNewName(effectiveDisplayName);
    }
  }, [autoStartRename, effectiveDisplayName]);

  const isVirtual = isVirtualFolderNode(node);

  // Helper to construct full path from parent path and new name
  const getFullPath = useCallback(
    (name: string) => {
      return parentPath ? `${parentPath}/${name}` : name;
    },
    [parentPath]
  );

  const ExpanderButton = (
    <IconButton
      aria-label="Expand"
      isDisabled={!hasChildren}
      onClick={(e) => {
        e.stopPropagation();
        setExpanded?.(node.id);
      }}
      icon={
        <SparkelIcon
          fixedWidth
          icon={expanded ? faCaretDown : faCaretRight}
          size="2xs"
        />
      }
      size={'2xs'}
      variant={'unstyled'}
      paddingLeft={'1px'}
      paddingRight={1}
    />
  );

  const isLeafNode = !shapeChildren;

  let nodeLabel =
    isDiffing && diff?.oldName && diff?.newName ? (
      <DiffText
        oldValue={getDisplayName(diff?.oldName)}
        newValue={getDisplayName(diff?.newName)}
      />
    ) : (
      effectiveDisplayName
    );

  if (isDiffing && diff?.eventType === EventType.Deleted) {
    nodeLabel = (
      <Text textDecoration="line-through">{effectiveDisplayName}</Text>
    );
  }

  const handleDragStart = useCallback(
    (e: React.DragEvent) => {
      // Check if this is a folder or autoshape being dragged
      if (node.__typename === 'ShapeFolder') {
        const dragData: DragData = {
          type: 'folder',
          folder: node,
          folderPath: node.name,
        };
        e.dataTransfer.setData('text/plain', JSON.stringify(dragData));
        e.dataTransfer.effectAllowed = 'move';
        return;
      }

      if (node.__typename === 'AutoShape') {
        const dragData: DragData = {
          type: 'autoshape',
          autoShape: node,
          autoShapePath: node.name,
        };
        e.dataTransfer.setData('text/plain', JSON.stringify(dragData));
        e.dataTransfer.effectAllowed = 'move';
        return;
      }

      // Shape dragging (existing behavior)
      const found = selectedShapesRef.current.find(
        (shape) => shape.id === node.id
      );
      const shapesToMove = found
        ? selectedShapesRef.current // If the shape is selected, drag the entire selection
        : [node as ShapeDeepFragment | SheetShapeDeepFragment]; // Otherwise, just drag the single node

      const dragData: DragData = { type: 'shapes', shapes: shapesToMove };
      e.dataTransfer.setData('text/plain', JSON.stringify(dragData));
      e.dataTransfer.effectAllowed = 'move';
    },
    [node, selectedShapesRef]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsHovered(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  // eslint-disable-next-line complexity
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsHovered(false);

      const rawData = e.dataTransfer.getData('text/plain');
      if (!rawData) return;

      const dragData = JSON.parse(rawData) as DragData;

      // Determine the target folder path (null if dropping outside folders)
      const targetFolderPath = !isLeafNode && !isVirtual ? node.name : null;

      // Handle folder being dropped
      if (dragData.type === 'folder') {
        if (!onMoveFolder) return;

        // Validate the drop
        if (!canDropFolderOnTarget(dragData.folderPath, targetFolderPath)) {
          return; // Invalid drop (into itself or descendant)
        }

        const newPath = computeNewPath(dragData.folderPath, targetFolderPath);
        onMoveFolder(dragData.folder.id, newPath);
        return;
      }

      // Handle autoshape being dropped
      if (dragData.type === 'autoshape') {
        if (!onMoveAutoShape) return;

        // Validate the drop (same logic as folders)
        if (!canDropFolderOnTarget(dragData.autoShapePath, targetFolderPath)) {
          return; // Invalid drop (into itself or descendant)
        }

        const newPath = computeNewPath(
          dragData.autoShapePath,
          targetFolderPath
        );
        onMoveAutoShape(dragData.autoShape.id, newPath);
        return;
      }

      // Handle shapes being dropped (existing behavior)
      // For backwards compatibility, also handle old format where shapes array was passed directly
      const shapesToMove =
        dragData.type === 'shapes'
          ? dragData.shapes
          : (dragData as unknown as (
              | ShapeDeepFragment
              | SheetShapeDeepFragment
            )[]);

      // Move all shapes in the selection to the new folder (or out of the folder)
      onMoveNode(
        shapesToMove,
        !isLeafNode && !isVirtual ? (node as ShapeFolderDeepFragment) : null
      );
    },
    [isLeafNode, isVirtual, node, onMoveNode, onMoveFolder, onMoveAutoShape]
  );

  const folderOutline =
    isHovered && !isLeafNode ? `2px solid ${ChakraColor.blue300}` : 'none';

  return (
    <Box
      width={'100%'}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragLeave={handleDragLeave}
      outline={folderOutline}
      outlineOffset={-2}
      borderRadius="md"
      position="absolute"
      // I have to put these properties in the style object otherwise if I pass them as direct Box props, chakra interprets them in a special way
      style={{ transform: `translateY(${top}px)`, height: height }}
    >
      {!isLeafNode ? (
        <>
          <NodeItemMemoized
            node={node}
            key={node.id}
            isHidden={isHidden}
            setPageNumber={setPageNumber}
            changeActiveSheet={changeActiveSheet}
            editBIMShape={editBIMShape}
            editSheetShape={editSheetShape}
            isSelected={isSelected}
            allChildrenSelected={allChildrenSelected}
            allChildrenHidden={allChildrenHidden}
            onSelect={onShapeSelect}
            level={level}
            isRenaming={isRenaming}
            setIsRenaming={setIsRenaming}
            onShow={onShow}
            onHide={onHide}
            onDelete={onDelete}
            setViewerMode={setViewerMode}
            removeShapesFromFolder={removeShapesFromFolder}
            deleteFolder={deleteFolder}
            groupShapesIntoFolder={groupShapesIntoFolder}
            canGroupSelectedShapes={canGroupSelectedShapes}
            ungroupFolder={ungroupFolder}
            isDiffing={isDiffing}
            diff={diff}
            isMoveable={
              node.__typename === 'ShapeFolder' ||
              node.__typename === 'AutoShape'
            }
            onDragStart={handleDragStart}
          >
            {ExpanderButton}
            {isRenaming ? (
              <Input
                value={newName}
                size="xs"
                width="100%"
                height={5}
                marginLeft={1}
                autoFocus
                variant={'filled'}
                borderRadius={0}
                onChange={(e) => setNewName(e.target.value)}
                onBlur={() => {
                  setIsRenaming(false);
                  onRenameComplete?.();
                  if (newName !== effectiveDisplayName) {
                    onRename(node, getFullPath(newName));
                  }
                }}
                onFocus={(e) => e.target.select()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setIsRenaming(false);
                    onRenameComplete?.();
                    if (newName !== effectiveDisplayName) {
                      onRename(node, getFullPath(newName));
                    }
                  }
                }}
              />
            ) : (
              <Text
                paddingLeft={2}
                wordBreak={'break-all'}
                noOfLines={1}
                opacity={!hasChildren ? 0.5 : 1}
              >
                {nodeLabel}
              </Text>
            )}
          </NodeItemMemoized>
          {expanded && (
            <>
              {shapeChildren?.map((child) => {
                return <MemoizedShapeItem key={child.node.id} {...child} />;
              })}
            </>
          )}
        </>
      ) : (
        <NodeItemMemoized
          key={node.id}
          node={node}
          isSelected={isSelected}
          allChildrenSelected={allChildrenSelected}
          allChildrenHidden={allChildrenHidden}
          isHidden={isHidden}
          setPageNumber={setPageNumber}
          changeActiveSheet={changeActiveSheet}
          editBIMShape={editBIMShape}
          editSheetShape={editSheetShape}
          onSelect={onShapeSelect}
          level={level}
          setViewerMode={setViewerMode}
          removeShapesFromFolder={removeShapesFromFolder}
          deleteFolder={deleteFolder}
          isMoveable={
            node.__typename === 'Shape' ||
            node.__typename === 'SheetShape' ||
            node.__typename === 'ShapeFolder' ||
            node.__typename === 'AutoShape'
          } // Shapes, folders, and autoshapes are draggable (not virtual folders)
          onDragStart={handleDragStart}
          isRenaming={isRenaming}
          setIsRenaming={setIsRenaming}
          onShow={onShow}
          onHide={onHide}
          onDelete={onDelete}
          groupShapesIntoFolder={groupShapesIntoFolder}
          canGroupSelectedShapes={canGroupSelectedShapes}
          ungroupFolder={ungroupFolder}
          isDiffing={isDiffing}
          diff={diff}
        >
          <IconFromShape shape={node} />
          {isRenaming ? (
            <Input
              value={newName}
              size="xs"
              width="100%"
              height={5}
              marginLeft={1}
              autoFocus
              variant={'filled'}
              borderRadius={0}
              onChange={(e) => setNewName(e.target.value)}
              onBlur={() => {
                setIsRenaming(false);
                onRenameComplete?.();
                if (newName !== effectiveDisplayName) {
                  onRename(node, getFullPath(newName));
                }
              }}
              onFocus={(e) => e.target.select()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setIsRenaming(false);
                  onRenameComplete?.();
                  if (newName !== effectiveDisplayName) {
                    onRename(node, getFullPath(newName));
                  }
                }
              }}
            />
          ) : (
            <Text marginLeft={3} wordBreak={'break-all'} noOfLines={1}>
              {nodeLabel}
            </Text>
          )}
        </NodeItemMemoized>
      )}
    </Box>
  );
}

type NodeItemProps = {
  node: ShapeNodeType;
  isSelected: boolean;
  allChildrenSelected?: boolean;
  allChildrenHidden?: boolean;
  onSelect: (node: ShapeNodeType) => void;
  children?: React.ReactNode;
  level: number;
  isMoveable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  isRenaming: boolean;
  setIsRenaming: (isRenaming: boolean) => void;
  onShow: (node: ShapeNodeType) => void;
  onHide: (node: ShapeNodeType) => void;
  onDelete: (node: ShapeNodeType) => void;
  isHidden: boolean;
  setPageNumber: (pageNumber: number) => void;
  changeActiveSheet: (sheetId: string) => void;
  editBIMShape: (shapeToEdit: string) => void;
  editSheetShape: (shapeToEdit: string) => void;
  setViewerMode: (mode: ViewerMode) => void;
  removeShapesFromFolder: (node: ShapeNodeType) => Promise<void>;
  deleteFolder: (id: string) => Promise<void>;
  groupShapesIntoFolder: () => Promise<void>;
  canGroupSelectedShapes?: boolean;
  ungroupFolder?: (folderId: string) => Promise<void>;
  isDiffing?: boolean;
  diff?: ShapeItemDiff;
};

// eslint-disable-next-line complexity
const NodeItem = ({
  node,
  isSelected,
  allChildrenSelected,
  isHidden,
  onSelect,
  children,
  level,
  isMoveable = false,
  onDragStart,
  isRenaming,
  setIsRenaming,
  onShow,
  onHide,
  onDelete,
  setPageNumber,
  changeActiveSheet,
  editBIMShape,
  editSheetShape,
  setViewerMode,
  removeShapesFromFolder,
  deleteFolder,
  groupShapesIntoFolder,
  canGroupSelectedShapes,
  ungroupFolder,
  isDiffing,
  diff,
}: NodeItemProps) => {
  const isAutoShapeChild =
    !!(node as ShapeDeepFragment).autoShapeId ||
    !!(node as SheetShapeDeepFragment).autoShapeId;
  const { sheetsViewerMode } = useEnabledSheetViewerModeValue();
  const menuItemColor = useColorModeValue('gray.800', 'gray.200');
  const hoverBackgroundColor = useColorModeValue('gray.50', 'gray.700');
  const selectedNodeBackgroundColor = useColorModeValue('blue.50', 'blue.800');
  const autoShapeColor = useColorModeValue('purple.500', 'purple.200');
  const selectedAutoShapeBackgroundColor = useColorModeValue(
    'purple.50',
    'purple.800'
  );
  const selectedColor = useColorModeValue('blue.700', 'blue.200');
  const selectedParentBackgroundColor = useColorModeValue(
    'blue.100',
    'blue.600'
  );
  const deleteColor = useColorModeValue('red.400', 'red.300');
  const { getTextColor, getBackgroundColor } = useDiffing();
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  // State for the edit autoshape popover (opened separately from the menu)
  const [isEditAutoshapeOpen, setIsEditAutoshapeOpen] = useState(false);
  const [preventClose, setPreventClose] = useState(false);
  const [anchorPosition, setAnchorPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  // Ref for anchoring the popover to the menu button
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  let selectedBackgroundColor = selectedNodeBackgroundColor;
  let nodeColor = menuItemColor;

  if (node.__typename === 'AutoShape' || isAutoShapeChild) {
    selectedBackgroundColor = selectedAutoShapeBackgroundColor;
    nodeColor = autoShapeColor;
  } else if (
    node.__typename === 'ShapeFolder' ||
    node.__typename === 'VirtualFolder' ||
    node.__typename === undefined
  ) {
    selectedBackgroundColor = selectedParentBackgroundColor;
  }

  if (
    allChildrenSelected &&
    !isSelected &&
    !isAutoShapeChild &&
    node.__typename !== 'AutoShape' &&
    node.__typename !== 'VirtualFolder'
  ) {
    selectedBackgroundColor = selectedNodeBackgroundColor;
    nodeColor = selectedColor;
  }

  let backgroundColor = isSelected ? selectedBackgroundColor : undefined;

  if (allChildrenSelected) backgroundColor = selectedBackgroundColor;

  if (isRenaming) backgroundColor = undefined;

  const eventType = diff?.eventType;

  if (isDiffing && eventType) {
    backgroundColor = getBackgroundColor(eventType);
  }

  if (isSelected && node.__typename !== 'AutoShape' && !isAutoShapeChild)
    nodeColor = selectedColor;

  if (isDiffing && eventType) {
    nodeColor = getTextColor(eventType);
  }

  const { t } = useTranslation(['common', 'project']);
  const { isProtan } = useUserTenant();

  const nodeStyle = {
    width: '100%',
    paddingTop: 4,
    paddingBottom: 4,
    borderRadius: 4,
    cursor: 'pointer',
  };

  const nodeHoverStyle = {
    backgroundColor: hoverBackgroundColor,
  };

  const contextMenuItems = useMemo(() => {
    const nodeType:
      | 'shape'
      | 'sheetShape'
      | 'folder'
      | 'autoShape'
      | 'virtualFolder'
      | 'unknown' = (() => {
      switch (node.__typename) {
        case 'Shape':
          return 'shape';
        case 'SheetShape':
          return 'sheetShape';
        case 'ShapeFolder':
          return 'folder';
        case 'AutoShape':
          return 'autoShape';
        case 'VirtualFolder':
          return 'virtualFolder';
        default:
          return 'folder';
      }
    })();

    // Virtual folders have no context menu actions
    if (nodeType === 'virtualFolder') {
      return [];
    }

    const isAutoShapeShape = !!(
      node as ShapeDeepFragment | SheetShapeDeepFragment
    ).autoShapeId;

    const items = [
      {
        icon: faCogs,
        visible: nodeType === 'autoShape',
        label: 'Auto Shape settings',
        onClick: () => {
          console.log('Auto Shape settings');
        },
      },
      {
        icon: faObjectGroup,
        // This item should be visible if selected (or all children selected for folders) and not an autoshape
        visible:
          (isSelected || allChildrenSelected) &&
          nodeType !== 'autoShape' &&
          !isProtan &&
          !isAutoShapeShape,
        // Disabled when selected shapes are from different parent folders
        disabled: !canGroupSelectedShapes,
        label: t('project:shapes.new-folder.create'),
        onClick: groupShapesIntoFolder,
      },
      {
        icon: faObjectUngroup,
        visible: nodeType === 'folder' && !isProtan,
        label: t('project:shapes.ungroup-folder'),
        onClick: async () => {
          await ungroupFolder?.(node.id);
        },
      },
      {
        icon: faObjectUngroup,
        visible:
          nodeType !== 'folder' &&
          !!(node as ShapeDeepFragment | SheetShapeDeepFragment).folder &&
          !isProtan,
        label: t('project:shapes.remove-from-folder'),
        onClick: async () => await removeShapesFromFolder(node),
      },
      {
        icon: faPencil,
        visible: !isProtan && !isAutoShapeShape,
        label: t('project:shapes.rename-shape'),
        onClick: () => {
          setIsRenaming(true);
        },
      },
      {
        icon: faEyeSlash,
        visible: !isHidden,
        label: t('project:shapes.hide-shape'),
        onClick: () => {
          onHide(node);
        },
      },
      {
        icon: faEye,
        visible: isHidden,
        label: t('project:shapes.show-shape'),
        onClick: () => {
          onShow(node);
        },
      },
      {
        icon: faPen,
        visible:
          nodeType !== 'folder' &&
          nodeType !== 'autoShape' &&
          (node as ShapeDeepFragment | SheetShapeDeepFragment).autoShapeId ===
            null,
        label: t('project:shapes.edit-shape'),
        onClick: () => {
          if (nodeType === 'shape') {
            setViewerMode(ViewerMode.Models);
            editBIMShape(node.id);
          } else {
            const sheetShape = node as SheetShapeDeepFragment;
            setViewerMode(sheetsViewerMode);
            changeActiveSheet(sheetShape.sheetId);
            setPageNumber(sheetShape.sheetPageNumber);
            editSheetShape(node.id);
          }
        },
      },
      {
        icon: faTrash,
        visible: !isAutoShapeShape,
        label: t('project:shapes.delete'),
        onClick: () => {
          onDelete(node);
        },
        color: deleteColor,
      },
    ];

    return items.filter((item) => item.visible);
  }, [
    node,
    isSelected,
    allChildrenSelected,
    t,
    groupShapesIntoFolder,
    canGroupSelectedShapes,
    isHidden,
    deleteColor,
    deleteFolder,
    removeShapesFromFolder,
    ungroupFolder,
    setIsRenaming,
    onHide,
    onShow,
    setViewerMode,
    editBIMShape,
    changeActiveSheet,
    setPageNumber,
    editSheetShape,
    onDelete,
    isProtan,
    sheetsViewerMode,
  ]);

  const isAutoShape = node.__typename === 'AutoShape';

  return (
    <HStack
      role="group"
      gap={0}
      draggable={isMoveable}
      onDragStart={isMoveable ? onDragStart : undefined}
      style={nodeStyle}
      _hover={isSelected || allChildrenSelected ? undefined : nodeHoverStyle}
      backgroundColor={backgroundColor}
      color={nodeColor}
      opacity={isHidden ? 0.5 : 1}
      paddingLeft={level * 24 + 4 + 'px'}
      paddingRight={isDiffing ? 1 : 0}
      fontWeight={isSelected ? 'bold' : undefined}
      onClick={() => {
        onSelect(node);
      }}
    >
      {children}
      {isRenaming || (
        <>
          {!isDiffing && (
            <>
              <Menu
                isOpen={isContextMenuOpen}
                onClose={() => setIsContextMenuOpen(false)}
                onOpen={() => setIsContextMenuOpen(true)}
              >
                <MenuButton
                  ref={menuButtonRef}
                  as={IconButton}
                  aria-label="Select"
                  marginLeft={'auto'}
                  size={'xs'}
                  variant={'unstyled'}
                  height={4}
                  opacity={0}
                  _groupHover={{ opacity: 1 }}
                  icon={<SparkelIcon icon={faEllipsis} />}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                />
                <Portal>
                  <MenuList
                    zIndex={99}
                    color={menuItemColor}
                    fontWeight={'normal'}
                  >
                    {contextMenuItems.map((item) => {
                      const key = `${item.label.toLowerCase()}-${node.id}`;
                      // For autoshape settings, close menu and open popover separately
                      if (
                        item.label === 'Auto Shape settings' &&
                        node.__typename === 'AutoShape'
                      ) {
                        return (
                          <MenuItem
                            key={key}
                            color={item.color}
                            icon={
                              <SparkelIcon
                                icon={item.icon}
                                color={item.color}
                              />
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              // Calculate anchor position before closing menu
                              if (menuButtonRef.current) {
                                const rect =
                                  menuButtonRef.current.getBoundingClientRect();
                                setAnchorPosition({
                                  x: rect.left + rect.width / 2,
                                  y: rect.top + rect.height / 2,
                                });
                                // Close the menu first, then open the popover
                                setIsContextMenuOpen(false);
                                // Use setTimeout to ensure menu closes before popover opens
                                setTimeout(() => {
                                  setIsEditAutoshapeOpen(true);
                                }, 50);
                              }
                            }}
                          >
                            {item.label}
                          </MenuItem>
                        );
                      }
                      return (
                        <MenuItem
                          key={key}
                          color={item.color}
                          isDisabled={item.disabled}
                          icon={
                            <SparkelIcon icon={item.icon} color={item.color} />
                          }
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            item.onClick();
                          }}
                        >
                          {item.label}
                        </MenuItem>
                      );
                    })}
                  </MenuList>
                </Portal>
              </Menu>
              {/* EditAutoshapePopover rendered outside the Menu, anchored to the menu button */}
              {node.__typename === 'AutoShape' && (
                <EditAutoshapePopover
                  autoShape={node}
                  isOpen={isEditAutoshapeOpen}
                  onClose={() => {
                    setIsEditAutoshapeOpen(false);
                    setAnchorPosition(null);
                  }}
                  onPreventCloseChange={setPreventClose}
                  anchorPosition={anchorPosition}
                />
              )}
            </>
          )}
          {isAutoShape && node.status !== AutoShapeStatus.Completed ? (
            <Tooltip label={node.errorMessage || node.status}>
              {/* Need box to properly pass ref */}
              <Box>
                <AutoShapeStatusIcon status={node.status} />
              </Box>
            </Tooltip>
          ) : (
            <IconButton
              aria-label="show/hide shape"
              marginLeft={isDiffing ? 'auto' : undefined}
              opacity={isHidden ? 1 : 0}
              _groupHover={{ opacity: 1 }}
              size="xs"
              variant={'unstyled'}
              height={4}
              icon={
                <SparkelIcon
                  icon={isHidden ? faEyeSlash : faEye}
                  color={menuItemColor}
                />
              }
              onClick={(e) => {
                e.stopPropagation();
                if (isHidden) {
                  onShow(node);
                } else {
                  onHide(node);
                }
              }}
            />
          )}
          {isDiffing && eventType && <DiffBadge eventType={eventType} />}
        </>
      )}
    </HStack>
  );
};

const AutoShapeStatusIcon = ({ status }: { status: AutoShapeStatus }) => {
  switch (status) {
    // Showing completed icon for testing
    case AutoShapeStatus.Completed:
      return <SparkelIcon icon={faCheckCircle} color={'green.400'} />;
    case AutoShapeStatus.Processing:
      return <Spinner size="xs" />;
    case AutoShapeStatus.Failed:
      return <SparkelIcon icon={faTimesCircle} color={'red.400'} />;
    case AutoShapeStatus.Pending:
      return <Spinner size="xs" color={'gray.400'} />; // Different color for testing
    default:
      return null;
  }
};

const IconFromShape = ({
  shape,
}: {
  shape:
    | ShapeDeepFragment
    | SheetShapeDeepFragment
    | ShapeFolderDeepFragment
    | AutoShapeDeepFragment
    | VirtualFolderNode;
}) => {
  // Folders (including virtual folders) don't have icons - they use expand/collapse buttons
  if (shape.__typename !== 'Shape' && shape.__typename !== 'SheetShape')
    return null;
  const iconColor = 'gray.400';
  if (shape.__typename === 'Shape') {
    // 3D Shape
    if (shape.polygon)
      return <SparkelIcon icon={faDrawPolygon} color={iconColor} />;
    if (shape.line) return <SparkelIcon as={PolylineIcon} color={iconColor} />;
    if (shape.shapePoint)
      return <SparkelIcon icon={faCircleDot} color={iconColor} />;
    if (shape.extrudedPolygon)
      return <SparkelIcon icon={faCube} color={iconColor} />;
  } else if (shape.__typename === 'SheetShape') {
    // 2D Shape
    const SheetIcon = ({ children }: { children: React.ReactElement }) => (
      <Center
        backgroundColor={'gray.50'}
        borderRadius={'sm'}
        border={'1px solid'}
        borderColor={'gray.300'}
        _dark={{ borderColor: 'gray.600', backgroundColor: 'gray.700' }}
        paddingX={1}
        marginLeft={-1}
        marginRight={-1.5}
        height={5}
      >
        {children}
      </Center>
    );

    if (shape.sheetShapePolygon)
      return (
        <SheetIcon>
          <SparkelIcon icon={faDrawPolygon} color={iconColor} />
        </SheetIcon>
      );
    if (shape.sheetShapeLine)
      return (
        <SheetIcon>
          <SparkelIcon as={PolylineIcon} color={iconColor} />
        </SheetIcon>
      );
    if (shape.sheetShapePoint)
      return (
        <SheetIcon>
          <SparkelIcon icon={faCircleDot} color={iconColor} />
        </SheetIcon>
      );
    if (shape.sheetShapeSplitLine)
      return (
        <SheetIcon>
          <SparkelIcon icon={faChevronDown} color={iconColor} />
        </SheetIcon>
      );
    if (shape.sheetShapeLegend)
      return (
        <SheetIcon>
          <SparkelIcon icon={faTableList} color={iconColor} />
        </SheetIcon>
      );
  }

  return <SparkelIcon icon={faDrawPolygon} color={iconColor} />;
};

const NodeItemMemoized = memo(NodeItem);

// eslint-disable-next-line complexity
const MemoizedShapeItem = memo(ShapeItem);
export default MemoizedShapeItem;

//export default ShapeItem;
